import {
  boundsContain,
  boundsIntersect,
  boundsOfPoints,
  bounds as boundsOfShape,
  distanceTo,
  fromPolar,
  polygonInBox,
  rotate,
  shapeInBox,
  sub,
  toRadians,
  unionBounds,
  vec,
  type Bounds,
  type BoxPlace,
  type Vec2,
} from '../geom';
import type { DimensionKind, DimensionNode } from '../model';
import { ANGLE } from './angle';
import { CALLOUT } from './callout';
import { LINEAR } from './linear';
import { DIAMETER, RADIUS } from './round';
import type { DimensionDefinition, DimensionLayout, DimensionText } from './types';

/** The node type whose `kind` includes `K`; radius and diameter share one. */
type NodeOf<K extends DimensionKind> = DimensionNode extends infer N
  ? N extends { readonly kind: infer Kinds }
    ? K extends Kinds
      ? N
      : never
    : never
  : never;

type Registry = { readonly [K in DimensionKind]: DimensionDefinition<NodeOf<K>> };

/** Every dimension kind the core understands, keyed by the node's `kind`, in record order. */
export const DIMENSIONS: Registry = {
  linear: LINEAR,
  radius: RADIUS,
  diameter: DIAMETER,
  angle: ANGLE,
  callout: CALLOUT,
};

export const DIMENSION_KINDS = Object.keys(DIMENSIONS) as DimensionKind[];

export function dimensionDefinition(kind: DimensionKind): DimensionDefinition {
  // Keyed by the same discriminant the node carries, so the lookup always matches.
  return DIMENSIONS[kind] as unknown as DimensionDefinition;
}

/** The text's four corners, from its center, size and angle. */
function corners(text: DimensionText): Vec2[] {
  const along = fromPolar(vec(0, 0), text.width / 2, text.angle);
  const up = fromPolar(vec(0, 0), text.height / 2, text.angle + 90);
  return [
    vec(text.at.x - along.x - up.x, text.at.y - along.y - up.y),
    vec(text.at.x + along.x - up.x, text.at.y + along.y - up.y),
    vec(text.at.x + along.x + up.x, text.at.y + along.y + up.y),
    vec(text.at.x - along.x + up.x, text.at.y - along.y + up.y),
  ];
}

export function layoutBounds(layout: DimensionLayout): Bounds {
  return [
    ...layout.strokes.map(boundsOfShape),
    boundsOfPoints([...corners(layout.text), ...layout.fills.flat()]),
  ].reduce(unionBounds);
}

/** How far `p` is from anything drawn; zero inside the text. */
export function layoutDistance(layout: DimensionLayout, p: Vec2): number {
  const { text } = layout;
  const local = rotate(sub(p, text.at), -toRadians(text.angle));
  const outside = Math.hypot(
    Math.max(Math.abs(local.x) - text.width / 2, 0),
    Math.max(Math.abs(local.y) - text.height / 2, 0),
  );
  return layout.strokes.reduce(
    (nearest, shape) => Math.min(nearest, distanceTo(shape, p)),
    outside,
  );
}

/**
 * Where a layout stands against a box, by what it draws: its strokes, its fills and its
 * text's area. A view's frame around the box is clear of it.
 */
export function layoutInBox(layout: DimensionLayout, box: Bounds): BoxPlace {
  const around = layoutBounds(layout);
  if (!boundsIntersect(around, box)) return null;
  if (boundsContain(box, around)) return 'inside';
  const { text } = layout;
  const touches =
    layout.strokes.some((shape) => shapeInBox(shape, box) !== null) ||
    layout.fills.some((fill) => polygonInBox(fill, box) !== null) ||
    (text.width > 0 && text.height > 0 && polygonInBox(corners(text), box) !== null);
  return touches ? 'crossing' : null;
}

export { DEFAULT_LOOK, lookOf, textAt } from './parts';
export type {
  DimensionDefinition,
  DimensionLayout,
  DimensionLook,
  DimensionText,
  Measured,
} from './types';
